import { Controller } from '@hotwired/stimulus'
import { readInheritableStaticValue } from 'util/class'

class VueController extends Controller {
  static vueComponent = ''

  async connect () {
    await this.loadVueComponent()
  }

  async disconnect () {
    await this.unloadVueComponent()
  }

  get componentKey () {
    return readInheritableStaticValue(this.constructor, 'vueComponent')
  }

  get vueTarget () {
    if (this.hasRootTarget) {
      return this.rootTarget
    }

    return this.element
  }

  // async load in vue js
  async loadVueComponent () {
    const {
      createApp,
      provide,
      h,
      defineComponent,
      reactive,
      nextTick
    } = await import('vue')

    // Dynamically import the Vue component
    const { default: component } = await import(`../components/${this.componentKey}.vue`)

    const Component = defineComponent(component)

    const handler = (eventName, data) => this.#handler(eventName, data)

    const reactiveProps = reactive(Object.assign({}, this.props))

    // create a new div to mount the vue component into
    const target = document.createElement('div')

    // once the component is mounted, it will be moved here
    const container = this.vueTarget

    const app = createApp({
      setup () {
        provide('handle', handler)
      },

      async mounted () {
        await nextTick()

        container.innerHTML = ''
        container.appendChild(target)
      },

      render: () => h(Component, reactiveProps)
    })

    app.config.globalProperties.controller = this

    this.reactiveProps = reactiveProps
    app.mount(target)

    this.instance = app
  }

  async unloadVueComponent () {
    if (this.instance) {
      this.instance.unmount()
      this.instance = null
    }
  }

  get props () {
    return {}
  }

  update (newProps) {
    for (const [key, value] of Object.entries(newProps)) {
      this.reactiveProps[key] = value
    }
  }

  #handler (eventName, data) {
    switch (eventName) {
      case 'value-changed':
        this.#handleValueChangedfromVue(data)
        break
    }
  }

  #handleValueChangedfromVue (data) {
    // iterate over all keys and values of data and set them to the controller
    for (const [key, value] of Object.entries(data)) {
      this[`${key}Value`] = value
    }
  }
}

export {
  VueController
}
